import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {BaseUserListComponent} from "../base-user-list.component";
import {PaginatedTableComponent} from "~shared/components/tables/paginated-table/paginated-table.component";
import {UntypedFormBuilder, UntypedFormGroup} from "@angular/forms";
import {User} from "~core/models";
import {
  ClubService,
  DefaultNotificationService,
  NavigationService,
  PaginationService,
  StorageService,
  UserManagementService,
  UserService
} from "~core/services";
import {Router} from "@angular/router";
import {ClubListTranslationsService, CustomerStatusTranslationsService, TooltipTranslationService} from "~shared/translations";
import {FormLabelTranslationService} from "~shared/components/form-fields/translations/form-label-translations.service";
import {TablesTranslations} from "~shared/components/tables/tables-translations.service";
import {NotificationType} from "~core/enum";

/**
 * Component responsible for club-overview administration.
 */
@UntilDestroy()
@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html'
})
export class UserListComponent extends BaseUserListComponent implements OnInit, OnDestroy {
  @ViewChild(PaginatedTableComponent) paginatedTable: PaginatedTableComponent;
  public options: UntypedFormGroup;

  public columns: Array<string> = [];
  public data: Array<User> = [];
  public keys: Array<Array<string>> = [];
  public baseApi: string;
  public customQueries: Array<any> = [];

  constructor(
    public notificationService: DefaultNotificationService,
    public navigationService: NavigationService,
    public userService: UserService,
    public paginationService: PaginationService,
    public router: Router,
    public customerStatusTranslations: CustomerStatusTranslationsService,
    public clubService: ClubService,
    public clubListTranslations: ClubListTranslationsService,
    public storage: StorageService,
    public flTranslations: FormLabelTranslationService,
    public userModalService: UserManagementService,
    public tooltipTranslations: TooltipTranslationService,
    public formBuilder: UntypedFormBuilder,
    public tablesTranslations: TablesTranslations,
  ) {
    super(notificationService, navigationService, userService, paginationService, router, customerStatusTranslations,
      clubService, clubListTranslations, storage, userModalService, flTranslations);
    this.navigationService.setCurrentBreadcrumbs(['globalSettings', 'globalUserList']);
  }

  public ngOnDestroy(): void {
  }

  /**
   * On init get the user id and load that user.
   */
  public ngOnInit(): void {
    this.options = this.formBuilder.group({
      clubSelected: 'allClubs',
      orSearchAll: ''
    });

    this.columns.push(
      this.tablesTranslations.i18nClubName,
      this.tablesTranslations.i18nRole,
      this.tablesTranslations.i18nUsername,
      this.tablesTranslations.i18nFirstName,
      this.tablesTranslations.i18nLastName,
      this.tablesTranslations.i18nStatus,
    );
    this.keys.push(['displayClubName', 'type', 'username', 'firstName', 'lastName', 'status']);

    if (this.authenticatedUser.isAdmin) {
      this.clubService.fetchAllClubs(this.authenticatedUser).pipe(untilDestroyed(this))
        .subscribe({
          next: (data: any) => {
            this.clubService.setListOfClubs(data.clubs);
            this.getGlobalUsers();
          },
          error: () => {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.clubListTranslations.i18nErrorFetchingClubs
            });
          }
        });
    } else {
      this.getGlobalUsers();
    }
  }

  /**
   * Updates the club-overview selection adn triggers the search
   */
  public updateClubSelection(): void {
    this.getGlobalUsers();
    this.paginatedTable.baseApi = this.baseApi;
    this.paginatedTable.customQueries = this.customQueries;
    this.triggerSearch();
  }

  /**
   * Triggers the search and updates the custom queries
   */
  public triggerSearch(): void {
    this.customQueries.push(this.options.value);
    this.paginatedTable.refetchData();
  }

  /**
   * Get the global users endpoint
   */
  public getGlobalUsers(): any {
    this.loading = true;
    const endpoint = this.userService.getGlobalUsersEndpoint(this.options.value);
    this.baseApi = endpoint[0];
    this.customQueries = endpoint[1];
    this.loading = false;
  }

  /**
   * Sets the new reminder list
   */
  public showUserList(users: Array<User>): void {
    this.data = users.map(user => new User(user, this.customerStatusTranslations));
  }

}
